import { Button, Dropdown } from '@heroui/react'
import { useFieldArray, useWatch } from 'react-hook-form'
import type { Control } from 'react-hook-form'

import { TextFieldControl } from '@/components/ui/form-fields'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import { ChevronDownIcon, PlusIcon, TrashIcon } from '@/components/ui/icons'

import type { ProductFormValues } from '../schemas'
import type { OptionSetSummary } from '../types'
import { translated } from '../types'

interface ProductOptionsFieldProps {
  control: Control<ProductFormValues>
  /** The tenant's saved axes, offered as a starting point. Empty is a normal state. */
  optionSets: OptionSetSummary[]
  /**
   * Called with the whole option list after a saved set is dropped in, so the
   * variants can be built from it. Handed the list rather than the one addition
   * because the caller needs every axis to cross, and reading it back out of the
   * form right after an append would race the append.
   */
  onOptionSetApplied?: (options: ProductFormValues['options']) => void
  isDisabled?: boolean
}

/**
 * The axes a product varies along — `Size: S, M, L`.
 *
 * These exist for the variants below them: the server checks every variant against
 * this list and rejects the whole product when one does not carry a value for each
 * axis. A product sold as a single thing needs none of this.
 */
export function ProductOptionsField({
  control,
  optionSets,
  onOptionSetApplied,
  isDisabled,
}: ProductOptionsFieldProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'options' })
  const current = useWatch({ control, name: 'options' })

  /**
   * A saved set is copied in, not linked to.
   *
   * The product stores its axes inline — there is no set id in the save body — so
   * the picker is a shortcut past the typing, and editing the row afterwards is
   * expected rather than a mistake. A set whose items were all blank would append
   * an axis the schema then rejects, so those are left out of the menu.
   *
   * Picking one also fills the variant grid: an axis on its own sells nothing, and
   * leaving the merchant with a named option and no rows is the state the form
   * then refuses to save.
   */
  const presets = optionSets
    .map((set) => ({
      id: set.id,
      name: translated(set.name, ''),
      values: set.items.map((item) => translated(item.name, '')).filter(Boolean),
    }))
    .filter((preset) => preset.name && preset.values.length > 0)

  function appendPreset(id: string) {
    const preset = presets.find((candidate) => candidate.id === id)
    if (!preset) return

    const option = { name: preset.name, values: preset.values.join(', ') }

    append(option)
    onOptionSetApplied?.([...(current ?? []), option])
  }

  return (
    <FormSection
      title="Options"
      description="What the variants differ by. Leave empty if the product is sold as one thing."
      action={
        <>
          {presets.length > 0 && (
            <Dropdown>
              <Dropdown.Trigger
                aria-label="Add a saved option set"
                isDisabled={isDisabled}
                className="border-border text-foreground hover:bg-surface-secondary inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors disabled:opacity-50"
              >
                Use option set
                <ChevronDownIcon width={13} height={13} />
              </Dropdown.Trigger>

              <Dropdown.Popover>
                <Dropdown.Menu
                  aria-label="Saved option sets"
                  onAction={(key) => appendPreset(String(key))}
                >
                  {presets.map((preset) => (
                    <Dropdown.Item key={preset.id} id={preset.id} textValue={preset.name}>
                      <span className="flex flex-col gap-0.5">
                        <span>{preset.name}</span>
                        <span className="text-muted text-[11px]">{preset.values.join(', ')}</span>
                      </span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          )}

          <Button
            variant="ghost"
            size="sm"
            isDisabled={isDisabled}
            onPress={() => append({ name: '', values: '' })}
            className="border-border h-8 shrink-0 rounded-full border px-3 text-xs"
          >
            <PlusIcon width={14} height={14} />
            Add option
          </Button>
        </>
      }
    >
      {fields.length === 0 ? (
        <FormSectionEmpty>
          No options — the product is sold as one thing at one price.
          {presets.length > 0 && ' Start from a saved option set, or add one by hand.'}
        </FormSectionEmpty>
      ) : (
        <ul className="divide-border border-border divide-y border-t">
          {fields.map((field, index) => (
            <li key={field.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start">
              <div className="sm:w-44">
                <TextFieldControl
                  control={control}
                  name={`options.${index}.name`}
                  label="Option"
                  placeholder="Size"
                  isDisabled={isDisabled}
                />
              </div>

              <div className="flex-1">
                <TextFieldControl
                  control={control}
                  name={`options.${index}.values`}
                  label="Values"
                  placeholder="Small, Medium, Large"
                  description="Separate values with commas."
                  isDisabled={isDisabled}
                />
              </div>

              <Button
                variant="ghost"
                isIconOnly
                size="sm"
                aria-label={`Remove option ${index + 1}`}
                isDisabled={isDisabled}
                onPress={() => remove(index)}
                className="text-muted hover:text-danger size-9 shrink-0 sm:mt-6"
              >
                <TrashIcon width={16} height={16} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </FormSection>
  )
}
